import React, { useState } from 'react';
import {
  Radio,
  Wifi,
  Waves,
  Bed,
  Moon,
  AlertTriangle,
  CheckCircle2,
  PhoneCall,
  Scale,
  Activity,
  Heart,
  Droplets,
  CalendarCheck,
  Sparkles,
  RefreshCw,
  Home,
  Building2,
  AlertOctagon,
  Eye,
  Sliders,
  DollarSign,
  Pill,
  Refrigerator,
  ShieldAlert,
  TestTube2,
  MessageSquareHeart,
  FileCheck,
  Download,
  Copy,
  Check,
  X
} from 'lucide-react';
import { RoomAmbientSensor, HomeRPMSenior, EarlyDeteriorationSignal, FamilyDigestSubject, ClinicalBusiness } from '../types';
import { generateRPMClinicalReview } from '../services/geminiService';
import { NativeMarkdownViewer } from './NativeMarkdownViewer';
import { EarlyDeteriorationSentryCard } from './EarlyDeteriorationSentryCard';

interface RemoteCareRPMProps {
  roomSensors: RoomAmbientSensor[];
  homeSeniors: HomeRPMSenior[];
  deteriorationSignals?: EarlyDeteriorationSignal[];
  onToggleFallWarning: (roomId: string) => void;
  onClearUTIAlert: (roomId: string) => void;
  onPlaceSentryOrder?: (signalId: string) => void;
  onOpenFamilyDigest: (subject: FamilyDigestSubject) => void;
  currentBusiness?: ClinicalBusiness;
  onOpenAccessModal?: () => void;
}

export const RemoteCareRPM: React.FC<RemoteCareRPMProps> = ({
  roomSensors,
  homeSeniors,
  deteriorationSignals = [],
  onToggleFallWarning,
  onClearUTIAlert,
  onPlaceSentryOrder,
  onOpenFamilyDigest,
  currentBusiness,
  onOpenAccessModal
}) => {
  const [activeSubView, setActiveSubView] = useState<'ambientFacility' | 'agingInPlace'>('ambientFacility');
  const [selectedHomeSenior, setSelectedHomeSenior] = useState<HomeRPMSenior>(homeSeniors[0]);
  const [aiClinicalNote, setAiClinicalNote] = useState<string>('');
  const [isGeneratingNote, setIsGeneratingNote] = useState<boolean>(false);
  const [filterWing, setFilterWing] = useState<string>('All');
  const [isCptLedgerOpen, setIsCptLedgerOpen] = useState<boolean>(false);
  const [copiedEdi, setCopiedEdi] = useState<boolean>(false);

  // Summary counts
  const totalFallAlerts = roomSensors.filter((s) => s.mmWaveRadar.status === 'Fall Warning').length;
  const totalUTIAlerts = roomSensors.filter((s) => s.bathroomTracker.utiRiskAlert).length;
  const outOfBedCount = roomSensors.filter((s) => s.mattressSleepSensor.state === 'Out of Bed').length;

  const cptCompliantCount = homeSeniors.filter((h) => h.cpt99454.isCompliantReimbursable).length;
  const totalEstimatedReimbursement = homeSeniors
    .filter((h) => h.cpt99454.isCompliantReimbursable)
    .reduce((sum, h) => sum + h.cpt99454.estimatedBillingAmount, 0);

  const filteredRoomSensors = roomSensors.filter((room) => {
    if (filterWing === 'All') return true;
    return room.wing === filterWing;
  });

  const handleGenerateRPMNote = async (senior: HomeRPMSenior) => {
    setSelectedHomeSenior(senior);
    setIsGeneratingNote(true);
    const note = await generateRPMClinicalReview(senior);
    setAiClinicalNote(note);
    setIsGeneratingNote(false);
  };

  const handleLaunchRoomDigest = (room: RoomAmbientSensor) => {
    const subject: FamilyDigestSubject = {
      id: room.roomId,
      name: room.residentName,
      recipientName: room.familyContact.name,
      recipientRelation: room.familyContact.relation,
      recipientPhone: room.familyContact.phone,
      sleepScoreOrState: `${room.mattressSleepSensor.sleepQualityPct}% quality (${room.mattressSleepSensor.state})`,
      vitalsSummary: `Heart Rate: ${room.mattressSleepSensor.heartRateMicro} bpm, Respiration: ${room.mattressSleepSensor.respirationRate} br/min`,
      nutritionActivitySummary: `Bathroom visits: ${room.bathroomTracker.visitsToday} today, Posture: ${room.mmWaveRadar.posture}`,
      locationOrRoom: `${room.roomName} (${room.wing})`
    };
    onOpenFamilyDigest(subject);
  };

  const handleLaunchHomeSeniorDigest = (senior: HomeRPMSenior) => {
    const subject: FamilyDigestSubject = {
      id: senior.id,
      name: senior.fullName,
      recipientName: senior.familyContact.name,
      recipientRelation: senior.familyContact.relation,
      recipientPhone: senior.familyContact.phone,
      sleepScoreOrState: 'Restful 8 hours sleep logged via ambient hub',
      vitalsSummary: `BP ${senior.cellularBP.systolic}/${senior.cellularBP.diastolic} mmHg (Status: ${senior.cellularBP.status}), Pulse ${senior.cellularBP.pulse} bpm, Weight: ${senior.cellularScale.weightLbs} lbs`,
      nutritionActivitySummary: `Breakfast fridge access at ${senior.adlActivity.lastFridgeTime}, Medication: ${senior.adlActivity.medComplianceStatus}`,
      locationOrRoom: senior.city
    };
    onOpenFamilyDigest(subject);
  };

  const facilitySentrySignal = deteriorationSignals.find((s) => s.location.includes('Wing'));
  const homeSentrySignal = deteriorationSignals.find((s) => s.location.includes('Residence'));

  return (
    <div className="space-y-6">
      {/* Top Remote Care Header & Mode Switcher */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-tr from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 text-cyan-300">
            <Radio className="h-6 w-6 animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-white">
                Remote Care & Ambient RPM Intelligence Hub
              </h2>
              <span className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 text-[10px] px-2 py-0.5 rounded font-mono font-semibold">
                CELLULAR & mmWAVE
              </span>
            </div>
            <p className="text-xs text-slate-400">
              Zero-wearable ambient radars, continuous micro-sleep telemetry, and cellular Medicare CPT 99454 trackers.
            </p>
          </div>
        </div>

        {/* Top Header Actions */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setIsCptLedgerOpen(true)}
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-bold text-xs transition shadow-md shadow-teal-500/20 active:scale-95 shrink-0"
            title="Open Medicare CPT 99454 Billing Compliance Ledger"
          >
            <FileCheck className="w-4 h-4" />
            <span>Open CPT 99454 Billing Ledger ({cptCompliantCount} Qualified)</span>
          </button>

          {/* Tab Switcher */}
          <div className="flex items-center bg-slate-950 p-1.5 rounded-xl border border-slate-800 text-xs">
            <button
              onClick={() => setActiveSubView('ambientFacility')}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg font-semibold transition ${
                activeSubView === 'ambientFacility'
                  ? 'bg-teal-500 text-slate-950 shadow-md font-bold'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Building2 className="h-4 w-4" />
              <span>Inpatient Ambient Radars</span>
              {(totalFallAlerts > 0 || totalUTIAlerts > 0) && (
                <span className="h-2 w-2 rounded-full bg-rose-500 animate-ping"></span>
              )}
            </button>

            <button
              onClick={() => setActiveSubView('agingInPlace')}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg font-semibold transition ${
                activeSubView === 'agingInPlace'
                  ? 'bg-cyan-500 text-slate-950 shadow-md font-bold'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Home className="h-4 w-4" />
              <span>Aging-in-Place & CPT 99454 (Harold, Eleanor, Arthur)</span>
              <span className="font-mono text-[10px] bg-slate-800 text-cyan-300 px-1.5 py-0.5 rounded ml-1 font-bold">
                {cptCompliantCount} Qualified
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* AI EARLY DETERIORATION SENTRY BANNER (Relevant to Current View) */}
      {activeSubView === 'ambientFacility' && facilitySentrySignal && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs px-1">
            <span className="font-mono uppercase text-slate-400 text-[11px] flex items-center gap-1.5 font-bold">
              <ShieldAlert className="h-4 w-4 text-rose-400" />
              <span>Ambient Radar & Bathroom Frequency Deterioration Intercept</span>
            </span>
            <span className="text-rose-400 font-mono text-[10px] font-semibold">
              Triad Sentry Active
            </span>
          </div>
          <EarlyDeteriorationSentryCard
            signal={facilitySentrySignal}
            onPlaceOrder={onPlaceSentryOrder ? onPlaceSentryOrder : () => {}}
          />
        </div>
      )}

      {activeSubView === 'agingInPlace' && homeSentrySignal && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs px-1">
            <span className="font-mono uppercase text-slate-400 text-[11px] flex items-center gap-1.5 font-bold">
              <ShieldAlert className="h-4 w-4 text-rose-400" />
              <span>Homebound Cellular Sensor Deterioration Intercept</span>
            </span>
            <span className="text-rose-400 font-mono text-[10px] font-semibold">
              Triad Sentry Active
            </span>
          </div>
          <EarlyDeteriorationSentryCard
            signal={homeSentrySignal}
            onPlaceOrder={onPlaceSentryOrder ? onPlaceSentryOrder : () => {}}
          />
        </div>
      )}

      {/* VIEW 1: FACILITY WING AMBIENT SENSORS */}
      {activeSubView === 'ambientFacility' && (
        <div className="space-y-6">
          {/* Top Quick Status Ribbon */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-xl flex items-center justify-between">
              <div>
                <span className="text-[10px] font-mono uppercase text-slate-400">Monitored Suites</span>
                <div className="text-2xl font-extrabold text-white mt-1">{roomSensors.length} Rooms</div>
                <div className="text-[11px] text-teal-400 font-mono mt-0.5">3 Wings Online</div>
              </div>
              <Wifi className="h-8 w-8 text-teal-400/40" />
            </div>

            <div className={`p-4 rounded-xl border flex items-center justify-between ${
              totalFallAlerts > 0 ? 'bg-rose-950/20 border-rose-500/40 text-rose-300' : 'bg-slate-900/60 border-slate-800'
            }`}>
              <div>
                <span className="text-[10px] font-mono uppercase text-slate-400">mmWave Fall Warnings</span>
                <div className={`text-2xl font-extrabold mt-1 ${totalFallAlerts > 0 ? 'text-rose-400' : 'text-slate-100'}`}>
                  {totalFallAlerts}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">Floor-level posture flags</div>
              </div>
              <AlertTriangle className={`h-8 w-8 ${totalFallAlerts > 0 ? 'text-rose-400 animate-pulse' : 'text-slate-600'}`} />
            </div>

            <div className={`p-4 rounded-xl border flex items-center justify-between ${
              totalUTIAlerts > 0 ? 'bg-amber-950/20 border-amber-500/40 text-amber-300' : 'bg-slate-900/60 border-slate-800'
            }`}>
              <div>
                <span className="text-[10px] font-mono uppercase text-slate-400">Bathroom UTI Flags</span>
                <div className={`text-2xl font-extrabold mt-1 ${totalUTIAlerts > 0 ? 'text-amber-400' : 'text-slate-100'}`}>
                  {totalUTIAlerts}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">&gt;6 visits/day threshold</div>
              </div>
              <Droplets className={`h-8 w-8 ${totalUTIAlerts > 0 ? 'text-amber-400' : 'text-slate-600'}`} />
            </div>

            <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-xl flex items-center justify-between">
              <div>
                <span className="text-[10px] font-mono uppercase text-slate-400">Mattress Sleep Status</span>
                <div className="text-2xl font-extrabold text-white mt-1">
                  {roomSensors.length - outOfBedCount} / {roomSensors.length}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">Currently Resting in Bed</div>
              </div>
              <Moon className="h-8 w-8 text-indigo-400/40" />
            </div>
          </div>

          {/* Wing Filter Bar */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <span className="text-slate-400 font-mono text-[11px]">Filter by Wing:</span>
              {['All', 'Wing-A Memory Care', 'Wing-B Assisted', 'Wing-C Rehab'].map((wing) => (
                <button
                  key={wing}
                  onClick={() => setFilterWing(wing)}
                  className={`px-3 py-1 rounded-lg border transition ${
                    filterWing === wing
                      ? 'bg-teal-500/20 text-teal-300 border-teal-500/40 font-semibold'
                      : 'bg-slate-900 text-slate-400 border-slate-800 hover:text-white'
                  }`}
                >
                  {wing}
                </button>
              ))}
            </div>
            <span className="text-slate-400 font-mono text-[11px]">
              60 GHz FMCW Radar • 100 Hz Telemetry
            </span>
          </div>

          {/* Room-by-Room Ambient Tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filteredRoomSensors.map((room) => {
              const isFallAlert = room.mmWaveRadar.status === 'Fall Warning';
              const isUTIAlert = room.bathroomTracker.utiRiskAlert;

              return (
                <div
                  key={room.roomId}
                  className={`rounded-2xl border p-5 transition relative overflow-hidden bg-slate-900/80 shadow-lg ${
                    isFallAlert
                      ? 'border-rose-500 shadow-rose-950/40 ring-1 ring-rose-500/50'
                      : isUTIAlert
                      ? 'border-amber-500/50 shadow-amber-950/20'
                      : 'border-slate-800 hover:border-slate-700'
                  }`}
                >
                  {/* Room Card Header */}
                  <div className="flex items-start justify-between pb-3 border-b border-slate-800">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-sm text-white">{room.roomName}</span>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-teal-300">
                          {room.wing}
                        </span>
                      </div>
                      <h3 className="text-sm font-bold text-slate-200 mt-1">
                        {room.residentName}{' '}
                        <span className="text-xs text-slate-400 font-normal">({room.residentAge}y)</span>
                      </h3>
                    </div>

                    <div className="flex flex-col items-end gap-1.5">
                      <span
                        className={`text-[10px] font-mono px-2.5 py-0.5 rounded font-bold flex items-center gap-1 ${
                          isFallAlert
                            ? 'bg-rose-500 text-slate-950 animate-bounce'
                            : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        }`}
                      >
                        {isFallAlert && <AlertTriangle className="h-3 w-3" />}
                        <span>{room.mmWaveRadar.status.toUpperCase()}</span>
                      </span>

                      <span className="text-[10px] font-mono text-slate-500">
                        Signal: {room.mmWaveRadar.signalDb} dBm
                      </span>
                    </div>
                  </div>

                  {/* 3 Core Sensors Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    {/* 1. mmWave Fall Radar */}
                    <div className={`p-3 rounded-xl border flex flex-col justify-between ${
                      isFallAlert ? 'bg-rose-950/30 border-rose-500/40 text-rose-200' : 'bg-slate-950/70 border-slate-800'
                    }`}>
                      <div>
                        <div className="flex items-center justify-between text-[10px] font-mono uppercase text-slate-400 mb-1">
                          <span className="flex items-center gap-1">
                            <Waves className="h-3.5 w-3.5 text-cyan-400" /> mmWave Radar
                          </span>
                        </div>
                        <div className="text-xs font-bold text-slate-200">
                          Posture: <span className={isFallAlert ? 'text-rose-400 font-extrabold' : 'text-teal-300'}>
                            {room.mmWaveRadar.posture}
                          </span>
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-2 font-mono">
                        Seen: {room.mmWaveRadar.lastDetected}
                      </div>
                    </div>

                    {/* 2. Under-Mattress Sleep Sensor */}
                    <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between text-[10px] font-mono uppercase text-slate-400 mb-1">
                          <span className="flex items-center gap-1">
                            <Bed className="h-3.5 w-3.5 text-indigo-400" /> Sleep Mat
                          </span>
                        </div>
                        <div className="text-xs font-bold text-slate-200">
                          State: <span className="text-indigo-300">{room.mattressSleepSensor.state}</span>
                        </div>
                        <div className="text-[11px] text-slate-300 mt-1 flex items-center justify-between">
                          <span>Resp Rate:</span>
                          <span className="font-mono font-bold text-teal-400">
                            {room.mattressSleepSensor.respirationRate} br/m
                          </span>
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1 font-mono">
                        BCG HR: {room.mattressSleepSensor.heartRateMicro} bpm
                      </div>
                    </div>

                    {/* 3. Bathroom Frequency & UTI Tracker */}
                    <div className={`p-3 rounded-xl border flex flex-col justify-between ${
                      isUTIAlert ? 'bg-amber-950/30 border-amber-500/40' : 'bg-slate-950/70 border-slate-800'
                    }`}>
                      <div>
                        <div className="flex items-center justify-between text-[10px] font-mono uppercase text-slate-400 mb-1">
                          <span className="flex items-center gap-1">
                            <Droplets className="h-3.5 w-3.5 text-amber-400" /> Bathroom Log
                          </span>
                        </div>
                        <div className="text-xs font-bold text-slate-200 flex items-center justify-between">
                          <span>Visits Today:</span>
                          <span className={`font-mono text-sm ${isUTIAlert ? 'text-amber-400 font-extrabold' : 'text-slate-200'}`}>
                            {room.bathroomTracker.visitsToday}
                          </span>
                        </div>
                        {isUTIAlert && (
                          <div className="text-[10px] font-bold text-amber-400 mt-1 bg-amber-500/20 px-1 py-0.5 rounded border border-amber-500/30">
                            UTI RISK ALERT (&gt;6)
                          </div>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1 font-mono">
                        Last: {room.bathroomTracker.lastVisitTime}
                      </div>
                    </div>
                  </div>

                  {/* Actions & Sensor Overrides */}
                  <div className="mt-4 pt-3 border-t border-slate-800 flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span className="text-[11px] text-slate-400">
                      Family Contact: <strong className="text-slate-300">{room.familyContact.name}</strong> ({room.familyContact.phone})
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleLaunchRoomDigest(room)}
                        className="px-3 py-1.5 rounded-lg bg-pink-500/15 hover:bg-pink-500/25 text-pink-300 border border-pink-500/30 text-xs font-semibold transition flex items-center gap-1.5 shadow-sm"
                      >
                        <MessageSquareHeart className="h-3.5 w-3.5" />
                        <span>Generate Family Digest</span>
                      </button>

                      {isUTIAlert && (
                        <button
                          onClick={() => onClearUTIAlert(room.roomId)}
                          className="px-2.5 py-1 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[11px] font-semibold transition"
                        >
                          Clear UTI
                        </button>
                      )}

                      <button
                        onClick={() => onToggleFallWarning(room.roomId)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition border ${
                          isFallAlert
                            ? 'bg-rose-500 hover:bg-rose-400 text-slate-950 border-rose-400 font-bold'
                            : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700'
                        }`}
                      >
                        {isFallAlert ? 'Resolve Warning' : 'Simulate Fall'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* VIEW 2: AGING-IN-PLACE HOME TELEMETRY */}
      {activeSubView === 'agingInPlace' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Homebound Seniors Registry */}
          <div className="lg:col-span-7 space-y-4">
            {/* CPT 99454 Overview Banner */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-950 border border-cyan-500/40 rounded-2xl p-5 shadow-lg">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
                    <CalendarCheck className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                      Medicare CPT 99454 Compliance Tracker
                      <span className="bg-emerald-500/20 text-emerald-300 text-[10px] px-2 py-0.5 rounded font-mono font-semibold">
                        {cptCompliantCount} / {homeSeniors.length} REIMBURSABLE
                      </span>
                    </h3>
                    <p className="text-xs text-slate-400">
                      Requires at least 16 days of cellular device transmissions per 30-day billing cycle.
                    </p>
                  </div>
                </div>

                <div className="text-right flex flex-col items-end gap-1.5">
                  <div>
                    <span className="text-[10px] font-mono uppercase text-slate-400 block">Estimated Monthly Value</span>
                    <div className="text-xl font-mono font-extrabold text-emerald-400">
                      ${totalEstimatedReimbursement.toFixed(2)}
                    </div>
                  </div>

                  {currentBusiness?.featuresUnlocked?.cptBillingLedger ? (
                    <button
                      onClick={() => setIsCptLedgerOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-slate-950 font-bold text-xs transition shadow-md shadow-teal-500/20"
                    >
                      <FileCheck className="w-3.5 h-3.5" />
                      <span>Export CMS-1500 Ledger</span>
                    </button>
                  ) : (
                    <button
                      onClick={onOpenAccessModal}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 text-[10px] font-mono transition"
                      title="CPT 99454 billing exports require Commercial Pro or Enterprise Unified license"
                    >
                      <span>🔒 CPT Export Locked (Beta Trial)</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Direct Inline CPT 99454 Compliance Table */}
            <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FileCheck className="h-4 w-4 text-emerald-400" />
                  <h4 className="font-bold text-white text-xs uppercase font-mono tracking-wider">
                    CPT 99454 Transmission &amp; Compliance Ledger
                  </h4>
                </div>
                <span className="text-[10px] font-mono text-slate-400">
                  Target: ≥16 transmission days / 30-day billing cycle ($56.40/pt)
                </span>
              </div>

              <div className="rounded-xl border border-slate-800 overflow-hidden bg-slate-950">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-900 border-b border-slate-800 text-[10px] font-mono text-slate-400 uppercase">
                    <tr>
                      <th className="p-3">Beneficiary</th>
                      <th className="p-3">Days Logged</th>
                      <th className="p-3">Monthly Target</th>
                      <th className="p-3">Cellular Data Readings</th>
                      <th className="p-3 text-right">Allowable Reimbursable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/80 font-mono text-slate-200">
                    {homeSeniors.map((s) => (
                      <tr key={s.id} className="hover:bg-slate-900/40">
                        <td className="p-3">
                          <div className="font-sans font-bold text-white">{s.fullName}</div>
                          <div className="text-[10px] text-slate-500 font-mono">MRN: {s.id} • {s.city}</div>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded font-bold ${s.cpt99454.isCompliantReimbursable ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'}`}>
                            {s.cpt99454.daysLoggedThisMonth} / 16 Days
                          </span>
                        </td>
                        <td className="p-3 text-slate-400">
                          {s.cpt99454.isCompliantReimbursable ? (
                            <span className="text-emerald-400 font-bold">✓ MET (QUALIFIED)</span>
                          ) : (
                            <span className="text-amber-400 font-bold">5 Days Remaining</span>
                          )}
                        </td>
                        <td className="p-3 text-[11px] text-slate-300">
                          BP: {s.cellularBP.systolic}/{s.cellularBP.diastolic} mmHg • Scale: {s.cellularScale.weightLbs} lbs
                        </td>
                        <td className="p-3 text-right font-extrabold text-emerald-400">
                          {s.cpt99454.isCompliantReimbursable ? `$${s.cpt99454.estimatedBillingAmount.toFixed(2)}` : '$0.00'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Seniors Card Stream */}
            <div className="space-y-3.5">
              {homeSeniors.map((senior) => {
                const isSelected = selectedHomeSenior.id === senior.id;
                const isCptReady = senior.cpt99454.isCompliantReimbursable;
                const hasRapidWeightGain = senior.cellularScale.rapidGainFlag;

                return (
                  <div
                    key={senior.id}
                    onClick={() => setSelectedHomeSenior(senior)}
                    className={`p-4 rounded-2xl border transition cursor-pointer relative ${
                      isSelected
                        ? 'bg-slate-900 border-cyan-500 shadow-xl shadow-cyan-950/20 ring-1 ring-cyan-500/40'
                        : 'bg-slate-950/70 border-slate-800/80 hover:border-slate-700'
                    }`}
                  >
                    {/* Header: Name, Location, Enrolled Program */}
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-slate-100 text-sm">{senior.fullName}</h4>
                          <span className="text-xs font-mono text-slate-400">({senior.age}y)</span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-cyan-300 border border-slate-700">
                            {senior.enrolledProgram}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5">{senior.city}</p>
                      </div>

                      {/* CPT 99454 Pill Badge */}
                      <div className="text-right">
                        <div
                          className={`inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded font-bold border ${
                            isCptReady
                              ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                              : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                          }`}
                        >
                          <CalendarCheck className="h-3 w-3" />
                          <span>
                            {senior.cpt99454.daysLoggedThisMonth}/{senior.cpt99454.targetDays} Days (
                            {isCptReady ? 'Reimbursable' : 'At Risk'})
                          </span>
                        </div>
                        <div className="text-[9px] font-mono text-slate-500 mt-0.5">
                          {senior.cpt99454.daysRemainingInCycle} days left in cycle
                        </div>
                      </div>
                    </div>

                    {/* Cellular Devices Grid: BP & Scale */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-3">
                      {/* Cellular Blood Pressure */}
                      <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs">
                        <div className="flex items-center justify-between text-slate-400 text-[10px] font-mono mb-1">
                          <span className="flex items-center gap-1">
                            <Heart className="h-3 w-3 text-rose-400" /> Cellular BP
                          </span>
                          <span className="text-slate-500">LTE</span>
                        </div>
                        <div className="font-mono font-bold text-sm text-slate-100">
                          {senior.cellularBP.systolic}/{senior.cellularBP.diastolic}{' '}
                          <span className="text-[9px] font-normal text-slate-400">mmHg</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          Pulse: {senior.cellularBP.pulse} bpm • {senior.cellularBP.status}
                        </div>
                      </div>

                      {/* Cellular Scale */}
                      <div className={`p-2.5 rounded-xl border text-xs ${
                        hasRapidWeightGain ? 'bg-amber-950/30 border-amber-500/40' : 'bg-slate-900 border-slate-800'
                      }`}>
                        <div className="flex items-center justify-between text-slate-400 text-[10px] font-mono mb-1">
                          <span className="flex items-center gap-1">
                            <Scale className="h-3 w-3 text-cyan-400" /> Cellular Scale
                          </span>
                          {hasRapidWeightGain && (
                            <span className="text-amber-400 font-bold text-[9px]">SURGE</span>
                          )}
                        </div>
                        <div className="font-mono font-bold text-sm text-slate-100">
                          {senior.cellularScale.weightLbs}{' '}
                          <span className="text-[9px] font-normal text-slate-400">lbs</span>
                        </div>
                        <div className={`text-[10px] mt-0.5 font-mono ${
                          hasRapidWeightGain ? 'text-amber-400 font-bold' : 'text-slate-400'
                        }`}>
                          7d Trend: {senior.cellularScale.trend7DayLbs > 0 ? '+' : ''}
                          {senior.cellularScale.trend7DayLbs} lbs
                        </div>
                      </div>

                      {/* ADL Activity Score (Fridge & Med Cabinet) */}
                      <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs">
                        <div className="flex items-center justify-between text-slate-400 text-[10px] font-mono mb-1">
                          <span className="flex items-center gap-1">
                            <Activity className="h-3 w-3 text-teal-400" /> ADL Score
                          </span>
                          <span className="font-mono font-bold text-teal-400">{senior.adlActivity.overallAdlScore}/100</span>
                        </div>
                        <div className="text-[11px] text-slate-300 flex items-center justify-between">
                          <span className="flex items-center gap-1 text-[10px] text-slate-400">
                            <Refrigerator className="h-3 w-3" /> Fridge:
                          </span>
                          <span className="font-mono text-slate-200">{senior.adlActivity.fridgeOpensToday} opens</span>
                        </div>
                        <div className="text-[11px] text-slate-300 flex items-center justify-between mt-0.5">
                          <span className="flex items-center gap-1 text-[10px] text-slate-400">
                            <Pill className="h-3 w-3" /> Meds:
                          </span>
                          <span className="font-mono text-[10px] text-teal-300">
                            {senior.adlActivity.medComplianceStatus}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Footer Row: Physician & Triage Note + Generate Family Digest Action */}
                    <div className="mt-3 pt-2.5 border-t border-slate-800/80 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                      <span className="text-[11px] text-slate-400">
                        Family: <strong className="text-slate-300">{senior.familyContact.name}</strong> ({senior.familyContact.phone})
                      </span>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLaunchHomeSeniorDigest(senior);
                          }}
                          className="px-3 py-1.5 rounded-lg bg-pink-500/15 hover:bg-pink-500/25 text-pink-300 border border-pink-500/30 text-xs font-semibold transition flex items-center gap-1.5 shadow-sm"
                        >
                          <MessageSquareHeart className="h-3.5 w-3.5 text-pink-400" />
                          <span>Generate Family Digest</span>
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateRPMNote(senior);
                          }}
                          className="px-2.5 py-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 text-xs font-semibold transition flex items-center gap-1"
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          <span>AI RPM Review</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column: AI RPM Telemetry Review & Protocol Engine */}
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-slate-900/80 border border-cyan-500/40 rounded-2xl p-5 shadow-xl space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">RPM Clinical Copilot</h3>
                    <p className="text-[11px] text-slate-400">
                      Gemini 2.5 Flash Telemetry Risk & Adherence Synthesis
                    </p>
                  </div>
                </div>
              </div>

              {/* Selected Senior Telemetry Snapshot */}
              <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 text-xs space-y-2">
                <div className="flex items-center justify-between font-semibold text-slate-200">
                  <span>{selectedHomeSenior.fullName}</span>
                  <span className="font-mono text-teal-400">{selectedHomeSenior.enrolledProgram}</span>
                </div>
                <div className="text-[11px] text-slate-400 leading-relaxed">
                  {selectedHomeSenior.nurseTriageNotes}
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1 font-mono text-[10px]">
                  <div className="p-1.5 rounded bg-slate-900 text-slate-300 border border-slate-800">
                    BP: {selectedHomeSenior.cellularBP.systolic}/{selectedHomeSenior.cellularBP.diastolic} mmHg
                  </div>
                  <div className="p-1.5 rounded bg-slate-900 text-slate-300 border border-slate-800">
                    Weight: {selectedHomeSenior.cellularScale.weightLbs} lbs ({selectedHomeSenior.cellularScale.trend7DayLbs > 0 ? '+' : ''}{selectedHomeSenior.cellularScale.trend7DayLbs} lbs)
                  </div>
                </div>
              </div>

              {/* Action Buttons: Generate AI Assessment and Generate Family Digest */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={() => handleGenerateRPMNote(selectedHomeSenior)}
                  disabled={isGeneratingNote}
                  className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-slate-950 font-bold py-2 px-3 rounded-xl transition flex items-center justify-center gap-1.5 text-xs shadow-md shadow-cyan-500/20 disabled:opacity-50"
                >
                  <Sparkles className={`h-3.5 w-3.5 ${isGeneratingNote ? 'animate-spin' : ''}`} />
                  <span>{isGeneratingNote ? 'Analyzing...' : 'Clinical RPM Review'}</span>
                </button>

                <button
                  onClick={() => handleLaunchHomeSeniorDigest(selectedHomeSenior)}
                  className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-slate-950 font-bold py-2 px-3 rounded-xl transition flex items-center justify-center gap-1.5 text-xs shadow-md shadow-pink-500/20"
                >
                  <MessageSquareHeart className="h-3.5 w-3.5" />
                  <span>Generate Family Digest</span>
                </button>
              </div>

              {/* Rendered Clinical Review */}
              {aiClinicalNote ? (
                <div className="p-4 rounded-xl bg-slate-950 border border-cyan-500/30 space-y-2">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-semibold text-cyan-300">RPM Coordinator Clinical Impression:</span>
                    <span className="text-[10px] font-mono text-slate-500">CPT 99457 Eligible</span>
                  </div>
                  <div className="p-3 bg-slate-900/90 rounded-lg border border-slate-800">
                    <NativeMarkdownViewer content={aiClinicalNote} />
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-slate-950/40 border border-dashed border-slate-800 text-center text-xs text-slate-500">
                  Click 'Clinical RPM Review' to generate a 3-point clinical review or 'Generate Family Digest' to compose an SMS for {selectedHomeSenior.familyContact.name}.
                </div>
              )}

              {/* Quick Remote Outreach Actions */}
              <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-xs">
                <span className="text-slate-400 text-[11px]">Direct Cellular Telehealth</span>
                <button
                  onClick={() => alert(`Initiating secure cellular telehealth call to ${selectedHomeSenior.fullName}`)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition font-semibold"
                >
                  <PhoneCall className="h-3.5 w-3.5 text-emerald-400" />
                  <span>Call Senior Hub</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CPT 99454 Billing Ledger & Claims Export Modal */}
      {isCptLedgerOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/80">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-cyan-500/20 border border-cyan-500/40 text-cyan-300">
                  <FileCheck className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-base flex items-center gap-2">
                    Medicare CPT 99454 Reimbursement Ledger
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold">
                      EDI-837P READY
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    Provider: {currentBusiness?.name || 'Metropolitan Memorial Trauma Hub'} • NPI: {currentBusiness?.activeNpi || '1942008412'} • Tier: {currentBusiness?.licenseTier.replace('_', ' ') || 'ENTERPRISE UNIFIED'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsCptLedgerOpen(false)}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content Body */}
            <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-[10px] font-mono text-slate-400 block uppercase">Qualifying Transmission Rule</span>
                  <div className="text-base font-extrabold text-white mt-1">≥16 Days / 30-Day Cycle</div>
                  <span className="text-[10px] text-emerald-400 font-mono">CMS Rule Verified</span>
                </div>
                <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-[10px] font-mono text-slate-400 block uppercase">Compliant Beneficiaries</span>
                  <div className="text-base font-extrabold text-cyan-400 mt-1">{cptCompliantCount} Qualified Seniors</div>
                  <span className="text-[10px] text-slate-400 font-mono">100% Cellular Connected</span>
                </div>
                <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-[10px] font-mono text-slate-400 block uppercase">Monthly Projected Claim</span>
                  <div className="text-base font-extrabold text-emerald-400 mt-1">${totalEstimatedReimbursement.toFixed(2)}</div>
                  <span className="text-[10px] text-teal-400 font-mono">CPT Rate: $118 / patient</span>
                </div>
              </div>

              {/* Table */}
              <div className="rounded-xl border border-slate-800 overflow-hidden bg-slate-950">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-900 border-b border-slate-800 text-[10px] font-mono text-slate-400 uppercase">
                    <tr>
                      <th className="p-3">Beneficiary</th>
                      <th className="p-3">Days Logged</th>
                      <th className="p-3">Target</th>
                      <th className="p-3">Cellular Data Transmissions</th>
                      <th className="p-3 text-right">Allowable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/80 font-mono text-slate-200">
                    {homeSeniors.map((s) => (
                      <tr key={s.id} className="hover:bg-slate-900/40">
                        <td className="p-3">
                          <div className="font-sans font-bold text-white">{s.fullName}</div>
                          <div className="text-[10px] text-slate-500 font-mono">MRN: {s.id}</div>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded font-bold ${s.cpt99454.isCompliantReimbursable ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                            {s.cpt99454.daysLoggedThisMonth} Days
                          </span>
                        </td>
                        <td className="p-3 text-slate-400">16 Days</td>
                        <td className="p-3 text-[11px] text-slate-300">
                          BP: {s.cellularBP.systolic}/{s.cellularBP.diastolic} mmHg • Weight: {s.cellularScale.weightLbs} lbs
                        </td>
                        <td className="p-3 text-right font-extrabold text-emerald-400">
                          {s.cpt99454.isCompliantReimbursable ? `$${s.cpt99454.estimatedBillingAmount.toFixed(2)}` : '$0.00'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/90 flex flex-wrap items-center justify-between gap-3">
              <span className="text-[11px] font-mono text-slate-500">
                Format: CMS-1500 / EDI-837P 5010A1 • HIPAA Certified
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const csvContent = "data:text/csv;charset=utf-8," +
                      "Beneficiary,MRN,DaysLogged,TargetDays,BillingAmount,Status\n" +
                      homeSeniors.map(s => `"${s.fullName}","${s.id}",${s.cpt99454.daysLoggedThisMonth},${s.cpt99454.targetDaysRequired},${s.cpt99454.isCompliantReimbursable ? s.cpt99454.estimatedBillingAmount : 0},"${s.cpt99454.isCompliantReimbursable ? 'COMPLIANT' : 'AT_RISK'}"`).join("\n");
                    const encodedUri = encodeURI(csvContent);
                    const link = document.createElement("a");
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", `CPT-99454-Ledger-${new Date().toISOString().slice(0, 10)}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download CSV Manifest</span>
                </button>
                <button
                  onClick={() => {
                    setCopiedEdi(true);
                    navigator.clipboard.writeText(`ISA*00*          *00*          *ZZ*${currentBusiness?.activeNpi || '1942008412'}*ZZ*MEDICARE       *${new Date().toISOString().slice(2, 10).replace(/-/g, '')}*1200*^*00501*000000001*0*P*:~GS*HC*${currentBusiness?.activeNpi || '1942008412'}*MEDICARE*${new Date().toISOString().slice(0, 10).replace(/-/g, '')}*1200*1*X*005010A1~ST*837*0001*005010A1~BHT*0019*00*1*${new Date().toISOString().slice(0, 10).replace(/-/g, '')}*1200*CH~NM1*41*2*${currentBusiness?.name || 'METROPOLITAN MEMORIAL'}*****XX*${currentBusiness?.activeNpi || '1942008412'}~CLM*CPT99454-BATCH*${totalEstimatedReimbursement}***11:B:1*Y*A*Y*Y~SV1*HC:99454*${totalEstimatedReimbursement}*UN*${cptCompliantCount}***1~SE*8*0001~GE*1*1~IEA*1*000000001~`);
                    setTimeout(() => setCopiedEdi(false), 2500);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-slate-950 text-xs font-bold transition shadow-md shadow-teal-500/20"
                >
                  {copiedEdi ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedEdi ? 'Copied EDI-837P!' : 'Copy EDI-837P 5010'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
